import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, SIGNED_OUT_EVENT } from './api';
import type { AcceptedInvite, AuthUser } from './types';

interface AuthState {
  /** undefined while checking, null when signed out. */
  user: AuthUser | null | undefined;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (body: { name: string; email: string; password: string; invite?: string }) => Promise<AcceptedInvite | null>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/** Who is signed in. The session itself is an httpOnly cookie the page never sees. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((r) => !cancelled && setUser(r.user))
      .catch(() => !cancelled && setUser(null));
    const onSignedOut = () => setUser(null);
    window.addEventListener(SIGNED_OUT_EVENT, onSignedOut);
    return () => {
      cancelled = true;
      window.removeEventListener(SIGNED_OUT_EVENT, onSignedOut);
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setUser((await api.login(email, password)).user);
  }, []);
  const signUp = useCallback(async (body: { name: string; email: string; password: string; invite?: string }) => {
    const r = await api.register(body);
    setUser(r.user);
    return r.accepted;
  }, []);
  const signOut = useCallback(async () => {
    await api.logout().catch(() => undefined);
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, signIn, signUp, signOut }), [user, signIn, signUp, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}

/**
 * Sign in / create account. `invite` is passed along when signing up from an
 * invite link; the server accepts it with the new account and `onAccepted` opens it.
 */
export function AuthForm({
  invite,
  intro,
  onAccepted,
}: {
  invite?: string;
  intro?: ReactNode;
  onAccepted?: (accepted: AcceptedInvite) => void;
}) {
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState<'in' | 'up'>(invite ? 'up' : 'in');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'in') await signIn(email, password);
      else {
        const accepted = await signUp({ name, email, password, invite });
        if (accepted) onAccepted?.(accepted);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const field =
    'focus-ring mt-1 w-full rounded-md bg-white/[0.07] px-3 py-2.5 text-base text-white ring-1 ring-white/15 placeholder:text-white/30 focus:bg-white/[0.1]';

  return (
    <div className="w-full max-w-sm">
      {intro}
      <div className="mb-5 flex rounded-full bg-white/10 p-0.5 text-sm" role="tablist" aria-label="Sign in or create an account">
        {(
          [
            ['in', 'Sign in'],
            ['up', 'Create account'],
          ] as const
        ).map(([m, label]) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={mode === m}
            onClick={() => {
              setMode(m);
              setError(null);
            }}
            className={`focus-ring min-h-10 flex-1 rounded-full px-4 py-1.5 font-semibold transition ${
              mode === m ? 'bg-white text-black' : 'text-white/65 hover:text-white'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="space-y-4">
        {mode === 'up' && (
          <label className="block text-sm font-medium text-white/75">
            Your name
            <input
              className={field}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              required
              maxLength={80}
            />
          </label>
        )}
        <label className="block text-sm font-medium text-white/75">
          Email
          <input
            className={field}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </label>
        <label className="block text-sm font-medium text-white/75">
          Password
          <input
            className={field}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={mode === 'in' ? 'current-password' : 'new-password'}
            minLength={mode === 'up' ? 8 : undefined}
            required
          />
          {mode === 'up' && <span className="mt-1 block text-xs text-white/40">At least 8 characters.</span>}
        </label>
        {error && (
          <p role="alert" className="text-sm text-red-300">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          className="focus-ring w-full rounded-md bg-accent px-5 py-3 text-sm font-bold text-white transition hover:bg-accent-soft disabled:opacity-50"
        >
          {busy ? 'One moment…' : mode === 'in' ? 'Sign in' : 'Create account'}
        </button>
      </form>
    </div>
  );
}

/** Full-page sign-in for signed-out visitors. */
export function SignInPage() {
  return (
    <div className="flex min-h-full flex-col items-center justify-center bg-ink-950 px-4 py-16">
      <div className="mb-8 flex items-center gap-2.5">
        <span className="text-accent text-4xl leading-none font-extrabold tracking-tight">M</span>
        <span className="text-2xl font-bold tracking-tight">
          Memory<span className="text-white/55">flix</span>
        </span>
      </div>
      <AuthForm intro={<p className="mb-6 text-center text-sm text-white/55">Your memories, as episodes. Sign in to watch.</p>} />
    </div>
  );
}
